import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { AlertsService } from '../utility-service/alerts-service';
import { ErrorMessageParserUtility } from './error-message-parser.utility';
import { IBadInputFieldDetails } from '../models/system-internals/alert-dialog-contacts';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler, OnDestroy {
  private alertService?: AlertsService;
  private readonly hasWindow = typeof window !== 'undefined';
  private readonly unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    event.preventDefault();
    this.handleError(event.reason ?? event);
  };

  constructor(private readonly injector: Injector, private readonly ngZone: NgZone, private readonly logger: NGXLogger) {
    if (this.hasWindow) {
      window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
    }
  }

  ngOnDestroy(): void {
    if (this.hasWindow) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    }
  }

  handleError(error: unknown): void {
    const normalized = this.normalizeError(error);
    this.logger.error('GlobalErrorHandler', normalized.logMessage, normalized.originalError ?? error);
    if (normalized.userMessage) {
      this.showAlert(normalized.userMessage);
    }
  }

  private normalizeError(error: unknown): NormalizedError {
    if (error instanceof HttpErrorResponse) {
      return {
        logMessage: `HTTP ${error.status || 0} ${error.url ?? ''}`.trim(),
        userMessage: ErrorMessageParserUtility.buildHttpUserMessage(error),
        originalError: error
      };
    }

    if (error instanceof Error) {
      return {
        logMessage: error.message,
        userMessage: 'An unexpected application error occurred. Please try again.',
        originalError: error
      };
    }

    if (typeof error === 'string') {
      return {
        logMessage: error,
        userMessage: error,
        originalError: error
      };
    }

    return {
      logMessage: 'Unknown error',
      userMessage: 'An unexpected error occurred. Please try again later.',
      originalError: error
    };
  }

  private showAlert(message: string | IBadInputFieldDetails[]): void {
    try {
      const alerts = this.getAlertService();
      if (!alerts) {
        return;
      }
      this.ngZone.run(() => {
        if(Array.isArray(message) && message.length === 0) {
          return;
        }
        if(typeof message === 'string') {
        alerts.showAlert(message, 'Error', 'Error').subscribe();
        } else {
          const validationErrors = message as IBadInputFieldDetails[];
          alerts.showBadInputFieldList('Data Validation Errors',  validationErrors).subscribe();
        }
      });
    } catch (alertError) {
      this.logger.error('GlobalErrorHandler', 'Failed to show alert for error', alertError);
    }
  }

  private getAlertService(): AlertsService | undefined {
    if (this.alertService) {
      return this.alertService;
    }

    try {
      this.alertService = this.injector.get(AlertsService);
    } catch {
      this.alertService = undefined;
    }
    return this.alertService;
  }
}

interface NormalizedError {
  logMessage: string;
  userMessage?: string | IBadInputFieldDetails[];
  originalError?: unknown;
}
